/**
 * Shift Assignment Manager Component
 * Assign shifts to employees with date ranges
 * 
 * Features:
 * - View all shift assignments (filterable by employee/shift)
 * - Create new assignments with effective date ranges
 * - Edit existing assignments
 * - Delete/deactivate assignments
 * - Validation for overlapping assignments
 * - Responsive table with accessibility
 */

import { useState, useEffect } from 'react';
import { useShiftAssignmentsStore } from '@/stores/shiftAssignmentsStore';
import { useShiftsStore } from '@/stores/shiftsStore';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash, User, Clock, Calendar } from '@phosphor-icons/react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { format } from 'date-fns';
import type { EmployeeShiftAssignment } from '@/types/shifts';
import type { ApiUser } from '@/lib/api';

// Validation schema
const assignmentFormSchema = z.object({
  user_id: z.string().min(1, 'Employee is required'),
  shift_schedule_id: z.string().min(1, 'Shift is required'),
  effective_from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format'),
  effective_until: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format').optional().or(z.literal('')),
}).refine(
  (data) => {
    if (!data.effective_until) return true;
    return new Date(data.effective_until) >= new Date(data.effective_from);
  },
  {
    message: 'End date must be after start date',
    path: ['effective_until'],
  }
);

type AssignmentFormData = z.infer<typeof assignmentFormSchema>;

export function ShiftAssignmentManager() {
  const { assignments, loading, error, fetchAssignments, createAssignment, updateAssignment, deleteAssignment } = useShiftAssignmentsStore();
  const { shifts, fetchShifts } = useShiftsStore();
  
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<EmployeeShiftAssignment | null>(null);
  const [deletingAssignment, setDeletingAssignment] = useState<EmployeeShiftAssignment | null>(null);
  const [employees, setEmployees] = useState<ApiUser[]>([]);
  const [filterUserId, setFilterUserId] = useState<string>('');
  const [filterShiftId, setFilterShiftId] = useState<string>('');

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
  } = useForm<AssignmentFormData>({
    resolver: zodResolver(assignmentFormSchema),
  });

  // Fetch data on mount
  useEffect(() => {
    fetchShifts(true); // Only active shifts
    fetchEmployees();
    fetchAssignments();
  }, [fetchShifts, fetchAssignments]);

  // Refetch when filters change
  useEffect(() => {
    const filters: any = { is_active: true };
    if (filterUserId) filters.user_id = filterUserId;
    if (filterShiftId) filters.shift_schedule_id = filterShiftId;
    fetchAssignments(filters);
  }, [filterUserId, filterShiftId, fetchAssignments]);

  // Fetch employees
  const fetchEmployees = async () => {
    try {
      const users = await api.getUsers();
      setEmployees(users);
    } catch (err) {
      toast.error('Failed to load employees');
    }
  };

  // Format date for display
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'Indefinite';
    try {
      return format(new Date(dateStr), 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  // Handle create assignment
  const onCreateSubmit = async (data: AssignmentFormData) => {
    try {
      const payload = {
        ...data,
        effective_until: data.effective_until || undefined,
      };
      await createAssignment(payload);
      toast.success('Shift assignment created successfully');
      setIsCreateOpen(false);
      reset();
    } catch (err) {
      toast.error((err as Error).message || 'Failed to create assignment');
    }
  };

  // Handle edit assignment
  const onEditSubmit = async (data: AssignmentFormData) => {
    if (!editingAssignment) return;
    
    try {
      const payload = {
        effective_from: data.effective_from,
        effective_until: data.effective_until || undefined,
      };
      await updateAssignment(editingAssignment.id, payload);
      toast.success('Assignment updated successfully');
      setEditingAssignment(null);
      reset();
    } catch (err) {
      toast.error((err as Error).message || 'Failed to update assignment');
    }
  };

  // Open edit dialog
  const handleEdit = (assignment: EmployeeShiftAssignment) => {
    setEditingAssignment(assignment);
    reset({
      user_id: assignment.user_id,
      shift_schedule_id: assignment.shift_schedule_id,
      effective_from: assignment.effective_from,
      effective_until: assignment.effective_until || '',
    });
  };

  // Handle delete assignment
  const handleDelete = async () => {
    if (!deletingAssignment) return;

    try {
      await deleteAssignment(deletingAssignment.id);
      toast.success('Assignment deleted successfully');
      setDeletingAssignment(null);
    } catch (err) {
      toast.error((err as Error).message || 'Failed to delete assignment');
    }
  };

  // Toggle assignment active status
  const handleToggleActive = async (assignment: EmployeeShiftAssignment) => {
    try {
      await updateAssignment(assignment.id, { is_active: assignment.is_active ? 0 : 1 });
      toast.success(`Assignment ${assignment.is_active ? 'deactivated' : 'activated'} successfully`);
    } catch (err) {
      toast.error((err as Error).message || 'Failed to update assignment status');
    }
  };

  return (
    <div className="space-y-6" role="region" aria-label="Shift Assignment Manager">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Shift Assignments</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Assign employees to shift schedules
          </p>
        </div>
        
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" weight="bold" />
              Assign Shift
            </Button>
          </DialogTrigger>
          <DialogContent aria-describedby="create-assignment-description">
            <DialogHeader>
              <DialogTitle>Create Shift Assignment</DialogTitle>
              <DialogDescription id="create-assignment-description">
                Assign an employee to a shift schedule with effective dates.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit(onCreateSubmit)} className="space-y-4">
              <div>
                <Label htmlFor="user_id">Employee *</Label>
                <Controller
                  name="user_id"
                  control={control}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger id="user_id" aria-invalid={!!errors.user_id}>
                        <SelectValue placeholder="Select employee" />
                      </SelectTrigger>
                      <SelectContent>
                        {employees.map((emp) => (
                          <SelectItem key={emp.id} value={emp.id}>
                            {emp.name} ({emp.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.user_id && (
                  <p className="text-sm text-destructive mt-1" role="alert">
                    {errors.user_id.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="shift_schedule_id">Shift *</Label>
                <Controller
                  name="shift_schedule_id"
                  control={control}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger id="shift_schedule_id" aria-invalid={!!errors.shift_schedule_id}>
                        <SelectValue placeholder="Select shift" />
                      </SelectTrigger>
                      <SelectContent>
                        {shifts.map((shift) => (
                          <SelectItem key={shift.id} value={shift.id}>
                            {shift.name} ({shift.start_time.substring(0, 5)} - {shift.end_time.substring(0, 5)})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.shift_schedule_id && (
                  <p className="text-sm text-destructive mt-1" role="alert">
                    {errors.shift_schedule_id.message}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="effective_from">Start Date *</Label>
                  <Input
                    id="effective_from"
                    type="date"
                    {...register('effective_from')}
                    aria-invalid={!!errors.effective_from}
                  />
                  {errors.effective_from && (
                    <p className="text-sm text-destructive mt-1" role="alert">
                      {errors.effective_from.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="effective_until">End Date (optional)</Label>
                  <Input
                    id="effective_until"
                    type="date"
                    {...register('effective_until')}
                    aria-invalid={!!errors.effective_until}
                  />
                  {errors.effective_until && (
                    <p className="text-sm text-destructive mt-1" role="alert">
                      {errors.effective_until.message}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    Leave empty for indefinite assignment
                  </p>
                </div>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsCreateOpen(false);
                    reset();
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Creating...' : 'Create Assignment'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Label htmlFor="filter-employee">Filter by Employee</Label>
          <Select value={filterUserId} onValueChange={setFilterUserId}>
            <SelectTrigger id="filter-employee">
              <SelectValue placeholder="All employees" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">All employees</SelectItem>
              {employees.map((emp) => (
                <SelectItem key={emp.id} value={emp.id}>
                  {emp.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1">
          <Label htmlFor="filter-shift">Filter by Shift</Label>
          <Select value={filterShiftId} onValueChange={setFilterShiftId}>
            <SelectTrigger id="filter-shift">
              <SelectValue placeholder="All shifts" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">All shifts</SelectItem>
              {shifts.map((shift) => (
                <SelectItem key={shift.id} value={shift.id}>
                  {shift.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded" role="alert">
          <p>{error}</p>
        </div>
      )}

      {/* Assignments Table */}
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[200px]">Employee</TableHead>
              <TableHead className="w-[180px]">Shift</TableHead>
              <TableHead className="w-[140px]">Effective From</TableHead>
              <TableHead className="w-[140px]">Effective Until</TableHead>
              <TableHead className="w-[100px]">Status</TableHead>
              <TableHead className="text-right w-[160px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  Loading assignments...
                </TableCell>
              </TableRow>
            ) : assignments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  No assignments found. Create your first assignment to get started.
                </TableCell>
              </TableRow>
            ) : (
              assignments.map((assignment) => (
                <TableRow key={assignment.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <div>{assignment.employee_name || 'Unknown'}</div>
                        <div className="text-xs text-muted-foreground">
                          {assignment.employee_department}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <div>{assignment.shift_name || 'Unknown'}</div>
                        <div className="text-xs text-muted-foreground">
                          {assignment.shift_start_time?.substring(0, 5)} - {assignment.shift_end_time?.substring(0, 5)}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      {formatDate(assignment.effective_from)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      {formatDate(assignment.effective_until)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={assignment.is_active ? 'default' : 'secondary'}>
                      {assignment.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEdit(assignment)}
                        aria-label={`Edit assignment for ${assignment.employee_name}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeletingAssignment(assignment)}
                        aria-label={`Delete assignment for ${assignment.employee_name}`}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editingAssignment} onOpenChange={() => setEditingAssignment(null)}>
        <DialogContent aria-describedby="edit-assignment-description">
          <DialogHeader>
            <DialogTitle>Edit Shift Assignment</DialogTitle>
            <DialogDescription id="edit-assignment-description">
              Update the assignment effective dates below.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit(onEditSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-effective-from">Start Date *</Label>
                <Input
                  id="edit-effective-from"
                  type="date"
                  {...register('effective_from')}
                  aria-invalid={!!errors.effective_from}
                />
                {errors.effective_from && (
                  <p className="text-sm text-destructive mt-1" role="alert">
                    {errors.effective_from.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="edit-effective-until">End Date (optional)</Label>
                <Input
                  id="edit-effective-until"
                  type="date"
                  {...register('effective_until')}
                  aria-invalid={!!errors.effective_until}
                />
                {errors.effective_until && (
                  <p className="text-sm text-destructive mt-1" role="alert">
                    {errors.effective_until.message}
                  </p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setEditingAssignment(null);
                  reset();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletingAssignment} onOpenChange={() => setDeletingAssignment(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete the shift assignment for {deletingAssignment?.employee_name}. 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
              Delete Assignment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
